import { CheckBox } from 'src/Components/Controls';
import { EnterQueueItemType } from 'src/Types/ReDoc/Enter';
import { EQueueType } from 'src/Types/ReDoc/EQueue';
import { IconButton, Modal, SearchInput } from 'src/Components';
import { IModalProps } from '..';
import { ReturnQueueItemType } from 'src/Types/ReDoc/Return';
import { useEffect, useState } from 'react';
import { useEQSelector } from 'src/Hooks';
import classNames from 'classnames';
import SimpleTable, { ColumnTypeSimple } from 'src/Components/SimpleTable';
import '../modals.scss';
import './CallByNumber.scss';

type ICallByNumberProps = IModalProps;

/** Массив с описанием колонок для таблицы с талонами */
const enterColumns: ColumnTypeSimple[] = [
	{
		id: 'timeCreated',
		title: 'Время выдачи',
		width: 165,
	},
	{
		id: 'name',
		title: 'Услуга',
	},
	{
		id: 'ticketNumber',
		title: 'Номер талона',
		type: 'number',
		width: 175,
	},
];

/** Компонента для отображения модального окна для выбора талона */
const CallByNumber: React.FC<ICallByNumberProps> = ({
	type,
	isOpen,
	onClose,
	onAction,
}) => {
	const queue =
		useEQSelector<(EnterQueueItemType | ReturnQueueItemType)[]>(
			type,
			'queue',
		) || [];
	const [talons, setTalons] = useState<
		(EnterQueueItemType | ReturnQueueItemType)[]
	>([]);
	const [selected, setSelected] = useState<string>('');
	const [filter, setFilter] = useState<string>('');
	const [isPersonal, setIsPersonal] = useState<boolean>(false);

	const isEnter = type === EQueueType.Enter;
	const isReturn = type === EQueueType.Return;
	const supportsQueueTable = isEnter || type === EQueueType.Return;

	useEffect(() => {
		if (isEnter) {
			if (!filter) {
				setTalons(
					queue?.filter((q) => {
						return (
							(q as EnterQueueItemType).isPersonal === isPersonal
						);
					}),
				);
			} else {
				const operations = queue.filter((q) => {
					const tn = q.ticketNumber.toLowerCase().trim();
					return (
						(q as EnterQueueItemType).isPersonal === isPersonal &&
						!!tn &&
						tn.indexOf(filter) !== -1
					);
				});
				setTalons(operations);
			}
			return;
		}
		if (isReturn) {
			setTalons(queue);
		}
	}, [queue, filter, isPersonal, isEnter, isReturn]);

	useEffect(() => {
		if (!isReturn || !isOpen) {
			return;
		}
		const timeoutId = window.setTimeout(() => {
			onAction('return-search-ticket', filter.trim());
		}, 300);
		return () => {
			return window.clearTimeout(timeoutId);
		};
	}, [filter, isReturn, isOpen, onAction]);

	const onSearchHandler = (value: string) => {
		if (supportsQueueTable) {
			setFilter(value);
		} else {
			setSelected(value);
		}
	};

	const selectHandler = (index: number) => {
		const selectedId = talons[index].id;
		if (isReturn) {
			onAction('inviteByNumber', selectedId);
			onClose();
			return;
		}
		setSelected(selectedId);
	};

	const onActionClickHandler = () => {
		onAction('inviteByNumber', selected);
		onClose();
	};

	const renderBody: React.FC = (props) => {
		return (
			<div
				className={classNames('modal_wrapper', {
					'modal_wrapper-callbn': supportsQueueTable,
				})}
			>
				<SearchInput
					onSearch={onSearchHandler}
					toLowerCase={supportsQueueTable}
					placeholder={
						supportsQueueTable
							? 'Введите номер талона'
							: 'Номер талона'
					}
				/>
				{supportsQueueTable && (
					<SimpleTable
						columns={enterColumns}
						data={talons}
						selectType="single"
						onSelectRow={selectHandler}
					/>
				)}
			</div>
		);
	};

	const renderModalToolbar = () => {
		return (
			<>
				{isEnter && (
					<CheckBox
						name="isPersonal"
						value={isPersonal}
						setValue={(v) => {
							return setIsPersonal(v);
						}}
					>
						Персональная очередь
					</CheckBox>
				)}
				<IconButton outline onClick={onClose}>
					Закрыть
				</IconButton>
				<IconButton disable={!selected} onClick={onActionClickHandler}>
					Выбрать
				</IconButton>
			</>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			title="Вызвать посетителя по талону"
			titleType={isReturn ? 'default' : 'large'}
			onClose={onClose}
			color="light"
			className={classNames('not_scrolled', {
				damask: !supportsQueueTable,
				'callbn-return': isReturn,
			})}
			renderFooter={isReturn ? undefined : renderModalToolbar()}
			renderBody={renderBody}
		/>
	);
};

export default CallByNumber;
